import _ from "lodash";
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { DataTable } from 'primereact/datatable';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import '../../platforms/platforms.css';
import {
    PLATFORM_OVERVIEW_METRIC_COLUMNS,
    PLATFORM_OVERVIEW_VOLUME_COLUMNS,
    currencyValues,
    formatCount,
    renderPerCurrency,
    sumByCurrency,
} from "./platformOverviewColumns";

// Figures right-aligned (see .ex-num in data.css) so digits line up down a column.
const NUMERIC_COLUMN = { align: 'right', headerClassName: 'ex-num', bodyClassName: 'ex-num' };

// Each platform's overview covers all its loans (no SME/CONSUMER split), so
// the toggle picks what to look at instead: volumes, or averages and rates.
// Two views keep each one narrow enough for the card. Values can't be
// `null`/`undefined` - SelectButton (like LendReportSections.js's
// SegmentToggle, the same pattern this mirrors) reports a falsy value on
// deselect, which the onChange guard below ignores.
const VIEW_OPTIONS = [
    { label: 'Volumes', value: 'volumes' },
    { label: 'Loan metrics', value: 'metrics' },
];

function PlatformsOverview({ linkable = true, ...props }) {
    // Was `useState(props.platformOverview)` - only ever read the prop at
    // mount, so it kept rendering the very first data it got (the
    // unfiltered, all-platforms list) even after data.js re-filtered and
    // passed down a new prop once View As switched to a single platform.
    const platformOverviewData = props.platformOverview;

    const [view, setView] = useState('volumes');
    const isVolumes = view === 'volumes';
    // DataTable's plain `globalFilter` string prop (not the newer `filters`
    // object API) - simplest fit here since the Platform column's `field`
    // is a render function when linkable (the NavLink), not a plain
    // string, which the newer per-column filter row can't match against
    // without extra `filterField` plumbing. globalFilterFields points
    // straight at the raw `platform` data key regardless.
    const [platformFilter, setPlatformFilter] = useState('');
    // Mirrors what the table is actually displaying (post-filter) so the
    // footer's Totals row adds up only the visible platforms, not every
    // platform regardless of the search box - DataTable's onValueChange
    // fires with exactly that filtered/sorted array.
    const [visibleRows, setVisibleRows] = useState(platformOverviewData);
    // Keeps visibleRows in sync whenever the prop itself changes (e.g. View
    // As switching platformOverviewData) - onValueChange below then narrows
    // it further whenever the search box actually filters something out.
    useEffect(() => setVisibleRows(platformOverviewData), [platformOverviewData]);
    const activeColumns = isVolumes ? PLATFORM_OVERVIEW_VOLUME_COLUMNS : PLATFORM_OVERVIEW_METRIC_COLUMNS;

    const dynamicColumnsPlatformOverview = activeColumns.map((col, i) => {
        if (col.field == 'platform') {
            // linkable=false (data.js, for a non-Exaloan account) - that
            // account only ever has this one row/platform anyway, so
            // there's nothing to drill into via a separate page; its own
            // detail renders inline instead (see PlatformOverviewDetailBody).
            // Plain text here rather than a dead-looking button.
            if (!linkable) {
                return <Column key={col.field} field={col.field} header={col.header} sortable={col.sortable} />
            }
            // Absolute path, not relative - this component used to only ever
            // render nested under /dashboard (old Dashboard.js), where a bare
            // relative `to="anodu"` correctly resolved to /dashboard/anodu.
            // Now that it's rendered from the Data page (/data) instead, that
            // same relative link resolved to the nonexistent /data/anodu,
            // silently bounced back to / by the catch-all route - the button
            // that "did nothing".
            return <Column key={col.field} field={row => (
                <NavLink className="ex-platform-link" to={`/dashboard/${_.kebabCase(row[col.field])}`}>
                    <span className="ex-platform-link-avatar" aria-hidden="true">{String(row[col.field] || '?').slice(0, 1).toUpperCase()}</span>
                    <span className="ex-platform-link-name" title={row[col.field]}>{row[col.field]}</span>
                    <i className="pi pi-arrow-right ex-platform-link-arrow" />
                </NavLink>
            )} header={col.header} body={col.body} sortable={col.sortable} />
        } else {
            return <Column key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable}
                {...(col.numeric ? NUMERIC_COLUMN : {})} />
        }
    })

    const loanCountTotal = () => formatCount(_.sumBy(visibleRows || [], (row) => row.total_loan_count || 0));

    // Visible platforms' volumes are in different currencies, so these are
    // totalled per currency, never added up - one line per currency, like the
    // row cells (a single "a EUR / b DKK / c NOK" line stretched every
    // volume column and pushed the table into a sideways scroll).
    const currencyTotal = (field) => renderPerCurrency(
        sumByCurrency((visibleRows || []).map((row) => currencyValues(row, field))));
    const numericFooter = (footer) => <Column footer={footer} footerClassName="ex-num" />;

    // One footer cell per data column (7, matching PLATFORM_OVERVIEW_VOLUME_COLUMNS).
    let volumeFooterGroup = <ColumnGroup>
        <Row>
            <Column footer="Totals:" footerStyle={{ textAlign: 'right' }} />
            {numericFooter(loanCountTotal())}
            {numericFooter(currencyTotal('origination_volume_since_3_months'))}
            {numericFooter(currencyTotal('origination_volume_since_6_months'))}
            {numericFooter(currencyTotal('origination_volume_since_12_months'))}
            {numericFooter(currencyTotal('origination_volume'))}
            <Column />
        </Row>
    </ColumnGroup>;

    // One footer cell per data column (8, matching
    // PLATFORM_OVERVIEW_METRIC_COLUMNS) - averages and rates don't add up,
    // so only the loan count gets a total; the rest are blank placeholders
    // so the column count still lines up with the body.
    let metricFooterGroup = <ColumnGroup>
        <Row>
            <Column footer="Totals:" footerStyle={{ textAlign: 'right' }} />
            {numericFooter(loanCountTotal())}
            <Column />
            <Column />
            <Column />
            <Column />
            <Column />
            <Column />
        </Row>
    </ColumnGroup>;

    return (
        <div className="ex-section ex-overview-section">
            <div className="ex-section-header">
                <div>
                    <h3 className="ex-section-title">Originators overview</h3>
                    <div className="ex-section-subtitle">All loans per Originator, in each loan tape's own currency</div>
                </div>
                <div className="ex-toolbar-group">
                    <SelectButton value={view} options={VIEW_OPTIONS} onChange={(e) => e.value && setView(e.value)} />
                    <span className="p-input-icon-left ex-search">
                        <i className="pi pi-search" />
                        <InputText value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} placeholder="Search by Originator" />
                    </span>
                </div>
            </div>
            <DataTable value={platformOverviewData} showGridlines dataKey="_key"
                globalFilter={platformFilter} globalFilterFields={['platform']} onValueChange={setVisibleRows}
                footerColumnGroup={isVolumes ? volumeFooterGroup : metricFooterGroup}
                paginator paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" rows={5} rowsPerPageOptions={[5, 10, 20, 50]} responsiveLayout="scroll"
                stripedRows className="ex-overview-table">
                {dynamicColumnsPlatformOverview}
            </DataTable>
        </div>
    );
};

export default PlatformsOverview;
